import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useActiveOrganization } from '@/features/organization/api/get-active-organization';

import { useCreateBlockedTime, useUpdateBlockedTime } from '../api';
import {
  type ClosureFormData,
  buildClosurePayload,
  closureDayCount,
  closureDefaults,
  closureFormSchema,
  closureFormValues,
} from '../closure/closure-form';

interface ClosureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The day the user is looking at, so the form opens on it. */
  startDate?: Date;
  /** When set, the dialog changes this closure instead of adding one. */
  editing?: {
    id: string;
    title: string;
    startDate: string | Date;
    endDate: string | Date;
  };
}

const toDate = (value: string) => (value ? parseISO(value) : undefined);
const toDay = (date: Date) => format(date, 'yyyy-MM-dd');

/** Close the branch for one or more whole days. */
export function ClosureDialog({
  open,
  onOpenChange,
  startDate,
  editing,
}: ClosureDialogProps) {
  const { data: organization } = useActiveOrganization();
  const timeZone = organization?.timezone ?? 'UTC';
  const { createBlockedTime, isCreating } = useCreateBlockedTime({
    successMessage: 'Closure added',
    onSuccess: () => onOpenChange(false),
  });
  const { updateBlockedTime, isUpdating } = useUpdateBlockedTime({
    successMessage: 'Closure updated',
    onSuccess: () => onOpenChange(false),
  });
  const form = useForm<ClosureFormData>({
    resolver: zodResolver(closureFormSchema),
    defaultValues: editing
      ? closureFormValues(editing, timeZone)
      : closureDefaults(startDate),
  });

  const firstDay = form.watch('firstDay');
  const lastDay = form.watch('lastDay');
  const days = lastDay >= firstDay ? closureDayCount({ firstDay, lastDay }) : 0;

  const onSubmit = (values: ClosureFormData) => {
    const payload = buildClosurePayload(values, timeZone);
    if (editing) updateBlockedTime({ id: editing.id, ...payload });
    else createBlockedTime(payload);
  };
  const isSaving = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit closure' : 'Add closure'}
            </DialogTitle>
            <DialogDescription>
              Close this branch for one or more days. Customers cannot book
              those days, and bookings reopen by themselves afterwards.
            </DialogDescription>
          </DialogHeader>

          <FieldGroup className="py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                name="firstDay"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>First day</FieldLabel>
                    <DatePicker
                      id={field.name}
                      value={toDate(field.value)}
                      onChange={(date) => {
                        if (!date) return;
                        const next = toDay(date);
                        field.onChange(next);
                        if (form.getValues('lastDay') < next) {
                          form.setValue('lastDay', next);
                        }
                      }}
                    />
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
              <Controller
                name="lastDay"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Last day</FieldLabel>
                    <DatePicker
                      id={field.name}
                      value={toDate(field.value)}
                      onChange={(date) => date && field.onChange(toDay(date))}
                    />
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
            </div>
            <Controller
              name="reason"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Reason</FieldLabel>
                  <Input
                    {...field}
                    id={field.name}
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.error && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <p className="text-sm text-muted-foreground">
              {days === 1 ? 'Closed for 1 day.' : `Closed for ${days} days.`}{' '}
              Existing appointments are not cancelled, so check for any on these
              dates.
            </p>
          </FieldGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : editing ? 'Save' : 'Add closure'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
